/**
 * useSmartBack — shared "sensible back button" hook.
 *
 * Fixes the classic SPA problem where a legal / support page's back button
 * hard-coded to `/` sends the user to the marketing landing page (which,
 * because it prominently shows the Sign-In CTA, is easily mistaken for the
 * Sign-In screen).
 *
 * Behaviour:
 *   1. If we have prior in-app history (React Router 6 gives the very first
 *      visited location a magic `key === 'default'` — anything else means
 *      the user arrived here via an in-app navigation), pop the history
 *      stack so they land on whatever screen they came from.
 *   2. Otherwise (deep-link, external URL, App Store reviewer, fresh
 *      Capacitor cold-start), fall back gracefully:
 *         • authenticated users → `/settings`
 *         • unauthenticated users → `/`  (Landing — public marketing)
 *
 * Works identically on web, iOS Capacitor and Android Capacitor because
 * React Router's history API is the same in every environment.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function useSmartBack(fallback = null) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  return useCallback(() => {
    // React Router 6 tags the initial browser entry with key === 'default'.
    // Any other value means we can safely pop.
    if (location.key && location.key !== 'default') {
      navigate(-1);
      return;
    }
    // Deep-link entry — pick a sensible landing point.
    if (fallback) {
      navigate(fallback, { replace: true });
      return;
    }
    navigate(user ? '/settings' : '/', { replace: true });
  }, [navigate, location.key, user, fallback]);
}
